import Release from './releaseResource';
import {
  transformReleases,
  transformRelease,
} from './transformer/releaseTransformer';
import transformToCamelCase from '../shared/transformers/toCamelCaseTransformer';
import transformError from '../shared/transformers/errorTransformer';
import transformToSnakeCase from '../shared/transformers/toSnakeCaseTransformer';

function createRelease({ packageName }) {
  return Release.save({ packageName }, {})
    .then(({ body: { release } }) => transformRelease(release))
    .catch(transformError);
}

function deleteRelease({ packageName, token }) {
  return Release.remove({ packageName, id: token }).catch(transformError);
}

function commitRelease({
  packageName,
  releaseId,
  changelogs,
  developerNote,
  stagedRolloutPercentage,
  file,
  releaseGroups,
}) {
  const formData = new FormData();
  if (file) formData.append('developer_file', file);
  formData.append('changelog_en', changelogs.changelogEn);
  formData.append('changelog_fa', changelogs.changelogFa);
  formData.append('developer_note', developerNote);
  formData.append('staged_rollout_percentage', stagedRolloutPercentage);
  if (releaseGroups) {
    formData.append('release_groups', releaseGroups);
  }

  return Release.commit(
    {
      packageName,
      id: releaseId,
    },
    formData,
  )
    .then(({ body: { release, warnings } }) => ({
      release: transformRelease(release),
      warnings,
    }))
    .catch(transformError);
}

function getRelease({ packageName }) {
  return Release.get({ packageName, committed: '1' })
    .then(({ body: { releases } }) => {
      return transformReleases(releases);
    })
    .catch(transformError);
}
function getUncommittedRelease({ packageName }) {
  return Release.getUncommitted({ packageName })
    .then(
      ({
        body: {
          releases: [unCommittedRelease],
        },
      }) => {
        return transformRelease(unCommittedRelease);
      },
    )
    .catch(transformError);
}

function addPackageToRelease({
  apk: { id: packageId },
  packageName,
  releaseId,
}) {
  return Release.addPackageToRelease(
    { packageName, id: releaseId },
    { apk_id: packageId },
  )
    .then(({ body: { package: packageItem, message } }) => ({
      package: transformToCamelCase(packageItem),
      message,
    }))
    .catch(transformError);
}

function removePackageFromRelease({
  packageName,
  package: packageItem,
  releaseId,
}) {
  return Release.removePackageFromRelease({
    packageName,
    packageToken: packageItem.token,
    id: releaseId,
  })
    .then(({ body: { message } }) => message)
    .catch(transformError);
}

function updateRelease({ packageName, releaseId, updateBody }) {
  const {
    changelogEn,
    changelogFa,
    autoPublish,
    stagedRolloutPercentage,
    releaseGroups,
  } = updateBody;

  return Release.update(
    { packageName, id: releaseId },
    {
      changelog_en: changelogEn,
      changelog_fa: changelogFa,
      auto_publish: autoPublish,
      staged_rollout_percentage: stagedRolloutPercentage,
      release_groups: releaseGroups,
    },
  )
    .then(({ body: { message = '', release } }) => {
      return { message, release: transformRelease(release) };
    })
    .catch(transformError);
}

function deactivateRelease({ releaseId, packageName }) {
  return Release.deactive({ id: releaseId, packageName }, {})
    .then(({ body: { release, message } }) => {
      return {
        release: transformRelease(release),
        message,
      };
    })
    .catch(transformError);
}

function publishRelease({ releaseId, packageName }) {
  return Release.publish({ id: releaseId, packageName }, {})
    .then(({ body: { message, release } }) => {
      return { message, release: transformRelease(release) };
    })
    .catch(transformError);
}

function reviewRequest({ id, packageName, developerNote, file }) {
  const formData = new FormData();
  if (file) formData.append('developer_file', file);
  formData.append('developer_note', developerNote);

  return Release.reviewRequest({ id, packageName }, formData)
    .then(({ body: { release, warnings } }) => ({
      release: transformRelease(release),
      warnings,
    }))
    .catch(transformError);
}

function cancelReviewRequest({ id, packageName }) {
  return Release.cancelReviewRequest({ id, packageName }, {})
    .then(({ body: { release } }) => transformRelease(release))
    .catch(transformError);
}

function updateStagedRollout({ id, packageName, percentage }) {
  return Release.updateStagedRollout(
    { id, packageName },
    { staged_rollout_percentage: percentage },
  )
    .then(({ body: { message, release } }) => ({
      release: transformRelease(release),
      message,
    }))
    .catch(transformError);
}

function getCpuArchitectures() {
  return Release.getCpuArchitectures()
    .then(({ body }) => body)
    .catch(transformError);
}

function updateCpuArchitectures({ packageName, token, architecture }) {
  return Release.updatePackageArchitecture(
    { packageName, token },
    { architecture },
  )
    .then(({ body }) => body)
    .catch(transformError);
}

function addBundleToRelease({ packageName, releaseId, token, requestedSign }) {
  return Release.addBundle(
    { packageName, id: releaseId },
    { token, requested_sign: requestedSign },
  )
    .then(({ body }) => transformToCamelCase(body))
    .catch(transformError);
}

function getReleaseGroup({ packageName }) {
  return Release.getGroup({ packageName })
    .then(({ body: { release_groups } }) => {
      const camelCaseReleaseGroups = transformToCamelCase(release_groups);
      return camelCaseReleaseGroups.map(item => item);
    })
    .catch(transformError);
}

function getReleaseMemberByID({ packageName, id }) {
  return Release.getReleaseMemberByID({ packageName, id })
    .then(({ body: { members } }) => {
      const camelCaseMembers = transformToCamelCase(members);
      return camelCaseMembers.map(item => item);
    })
    .catch(transformError);
}

function createGroup({ packageName, name, enabled, member_phones }) {
  return Release.createGroup({ packageName }, { name, enabled, member_phones })
    .then(({ body }) => transformToCamelCase(body))
    .catch(transformError);
}
function updateGroup({ packageName, id, name, enabled, member_phones }) {
  return Release.updateGroup(
    { packageName, id },
    { name, enabled, member_phones },
  )
    .then(({ body }) => transformToCamelCase(body))
    .catch(transformError);
}
function removeGroup({ packageName, id }) {
  return Release.removeGroup({ packageName, id })
    .then(({ body }) => transformToCamelCase(body))
    .catch(transformError);
}
function removeGroupMember({ packageName, id, phone_number }) {
  return Release.removeGroupMember({ packageName, id }, { phone_number })
    .then(({ body }) => transformToCamelCase(body))
    .catch(transformError);
}
function getReleaseGroupById({ packageName, id }) {
  return Release.getGroupById({ packageName, id })
    .then(({ body: { release_group } }) => {
      const camelCaseReleaseGroups = transformToCamelCase(release_group);
      return camelCaseReleaseGroups;
    })
    .catch(transformError);
}

export default {
  createRelease,
  deleteRelease,
  commitRelease,
  getRelease,
  addPackageToRelease,
  removePackageFromRelease,
  updateRelease,
  deactivateRelease,
  getUncommittedRelease,
  publishRelease,
  reviewRequest,
  cancelReviewRequest,
  updateStagedRollout,
  getCpuArchitectures,
  updateCpuArchitectures,
  addBundleToRelease,
  getReleaseGroup,
  createGroup,
  getReleaseMemberByID,
  updateGroup,
  removeGroup,
  removeGroupMember,
  getReleaseGroupById,
};
